import { api, downloadFile } from '@/lib/api';
import type { Company, PagedResponse } from './types';

/** How the company list and its Excel export are narrowed. Dates are
 *  'yyyy-MM-dd' and **inclusive** at both ends; the server widens each `To`
 *  past the end of that day, since the columns are DATETIME. */
export interface CompanyFilters {
  search?: string;
  startFrom?: string | null;
  startTo?: string | null;
  expireFrom?: string | null;
  expireTo?: string | null;
}

/** Only the filters that are actually set. An empty `startFrom=` is not the
 *  same as an absent one — the server fails to parse it as a date and 400s. */
function filterParams(filters: CompanyFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    const v = value?.trim();
    if (v) params.set(key, v);
  }
  return params;
}

export function listCompanies(page: number, pageSize: number, filters: CompanyFilters, base: string = '/api/companies') {
  const params = filterParams(filters);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  return api.get<PagedResponse<Company>>(`${base}?${params.toString()}`);
}

export interface UpdateCompanyBody {
  taxCode: string | null;
  approveNo: string | null;
  approveDate: string | null; // 'yyyy-MM-dd'
}
export function updateCompany(id: number, body: UpdateCompanyBody, base: string = '/api/companies') {
  return api.patch<Company>(`${base}/${id}`, body);
}

export interface AdminCredentials { username: string; password: string; created: boolean }
export function provisionAdmin(id: number, base: string = '/api/companies') {
  return api.post<AdminCredentials>(`${base}/${id}/admin-user`);
}

export type CompanyType = 'accounting' | 'tax';
export interface CreateCompanyBody { nameLa: string; nameEn: string; taxCode: string | null; companyType: CompanyType }
export interface CreatedCompany { id: number; pathName: string; adminUsername: string; adminPassword: string }
export function createCompany(body: CreateCompanyBody) {
  return api.post<CreatedCompany>('/api/companies', body);
}

export interface LastUsed { lastUsedAt: string | null }
export function getLastUsed(companyId: number, base: string = '/api/companies') {
  return api.get<LastUsed>(`${base}/${companyId}/last-used`);
}

export interface Subscription { id: number; isSubscripted: boolean; startDate: string | null; endDate: string | null; notiDate: string | null }
export function listSubscriptions(companyId: number, base: string = '/api/companies') {
  return api.get<Subscription[]>(`${base}/${companyId}/subscriptions`);
}
export function setSubscription(companyId: number, subId: number, isSubscripted: boolean, base: string = '/api/companies') {
  return api.put<void>(`${base}/${companyId}/subscriptions/${subId}`, { isSubscripted });
}

export interface CompanyListItem { id: number; companyName: string | null; databaseName: string | null; companyType: string | null }
export function listCompanyList(companyId: number, base: string = '/api/companies') { return api.get<CompanyListItem[]>(`${base}/${companyId}/company-list`); }
export function setCompanyListMain(companyId: number, databaseName: string, base: string = '/api/companies') { return api.put<void>(`${base}/${companyId}/company-list/main`, { databaseName }); }

export function createChild(mainId: number, body: CreateCompanyBody) { return api.post<CreatedCompany>(`/api/companies/${mainId}/children`, body); }

/** Which TaxRIS gateway a company's invoices go to.
 *  - `default`     — follow the accounting service's USE_NEW_TAXRIS setting
 *  - `new`         — the new HMAC-signed eTax Invoice Gateway
 *  - `legacy`      — the old hash-key endpoint
 *  - `unavailable` — READ ONLY: that tenant has not run its schema update yet
 *    (it runs on the tenant's first login after a deploy), so there is nothing
 *    to write to.
 *  - `unprovisioned` — READ ONLY: the column is there but the tenant has no
 *    `setting` row at all. A login does NOT fix this one — run_updates adds
 *    columns, never rows — so it needs a human. */
export type TaxrisGateway = 'default' | 'new' | 'legacy' | 'unavailable' | 'unprovisioned';
export interface TaxrisGatewayResponse { gateway: TaxrisGateway }
/** The choices an operator can actually store. */
export type Writable = Exclude<TaxrisGateway, 'unavailable' | 'unprovisioned'>;
export function getTaxrisGateway(companyId: number, base: string = '/api/companies') {
  return api.get<TaxrisGatewayResponse>(`${base}/${companyId}/taxris-gateway`);
}
export function setTaxrisGateway(companyId: number, gateway: Writable, base: string = '/api/companies') {
  return api.put<void>(`${base}/${companyId}/taxris-gateway`, { gateway });
}

/** The optional modules a company's accounting app shows. Per module:
 *  - `on` / `off`    — the tenant has that `setting` column and it reads so
 *  - `unavailable`   — READ ONLY: the tenant has no column for this module yet.
 *    The accounting service adds it in its schema update, which runs on that
 *    tenant's first login after a deploy. This is per module on purpose: the
 *    columns arrived in waves, so most tenants have sale/purchase/warehouse
 *    while only a few have `pos` — one blanket "not upgraded" would hide the
 *    modules they can actually use. */
export type ModuleState = 'on' | 'off' | 'unavailable';
export type ModuleKey = 'sale' | 'purchase' | 'warehouse' | 'pos';
/** Why the modules read the way they did. `unavailable` — the tenant has no
 *  `setting` table (a login fixes it); `unprovisioned` — it has no `setting`
 *  row (a login does NOT: the schema update adds columns, never rows). */
export type ModulesStatus = 'ready' | 'unavailable' | 'unprovisioned';
export interface TenantModulesResponse {
  status: ModulesStatus;
  modules: Record<ModuleKey, ModuleState>;
}
export function getTenantModules(companyId: number, base: string = '/api/companies') {
  return api.get<TenantModulesResponse>(`${base}/${companyId}/modules`);
}
/** Sends only the modules that changed: an omitted module is left untouched,
 *  and naming one the tenant has no column for would reject the whole save. */
export function setTenantModules(
  companyId: number,
  changes: Partial<Record<ModuleKey, boolean>>,
  base: string = '/api/companies',
) {
  return api.put<void>(`${base}/${companyId}/modules`, changes);
}

export function downloadBackup(id: number, base: string = '/api/companies') {
  return downloadFile(`${base}/${id}/backup`, 'backup.sql');
}

/** Excel export of the whole registry the page is showing, narrowed by the
 *  same filters as the list. Uses `downloadFile` rather than an anchor
 *  navigation so a failure surfaces as a toast; the workbook is small enough
 *  to buffer. */
export async function downloadCompaniesExport(filters: CompanyFilters, base: string = '/api/companies') {
  const params = filterParams(filters).toString();
  const headers = await downloadFile(`${base}/export${params ? `?${params}` : ''}`, 'companies.xlsx');
  // The body is the file the browser saved, so a short workbook looks exactly
  // like a complete one — the server says so in a header instead.
  return { truncated: headers.get('X-Export-Truncated') === 'true' };
}

/** Trigger a native browser download of the all-databases backup ZIP via an
 *  anchor navigation, so the browser streams straight to disk instead of
 *  buffering the whole (potentially multi-GB at fleet scale) archive in a Blob.
 *  Same-origin, so the auth cookie is sent automatically. Per-database failures
 *  are listed in `_backup_report.txt` inside the ZIP (not surfaced here — the
 *  streamed response can't carry a failure count in its headers). */
export function startAllBackupsDownload(): void {
  const a = document.createElement('a');
  a.href = '/api/backups/all';
  a.download = '';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
